import React, { useEffect, useState } from 'react';
import { Box, Grid, Divider, SxProps } from '@mui/material';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { CandidateApplicationsActivitySkeleton } from './candidateApplicationsActivitySkeleton';
import {
  mapAppliedCandidateDetailsData,
  mapCandidateApplicationFormData,
} from './candidateApplicationDetails.helper';
import { ICandidateProfileApplicationHistory } from 'modules/hiring/models/candidateProfile.model';
import LabelValuePair from '../labelValuePair';

export interface ICandidateProfileApplicationDetails {
  data: ICandidateProfileApplicationHistory | undefined;
  loading: boolean;
}

const pairDisplayStyle = {
  sx: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
  },
};

export interface ICandidateApplicationDetailsBlock {
  title: string;
  data: any;
  titleSx?: SxProps;
  gridLayout?: number[];
}

export interface ICandidateApplicationDataObject {
  [k: string]: {
    label: string;
    value: string | undefined | React.ReactNode;
  }[];
}

export const CandidateApplicationDetailsBlock: React.FC<
  ICandidateApplicationDetailsBlock
> = ({ title, data, titleSx, gridLayout }) => {
  return (
    <Grid
      container
      id='candidate-profile-application-form-container'
      sx={{
        marginBottom: '15px',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #F2F2F2',
        background: '#F8F9FB',
      }}
    >
      <TypographyWithI18N
        className='kfic2-smallHeader'
        id={`${title}-heading`}
        data-testid={`${title}-heading`}
        i18nKey={title}
        variant='h3'
        sx={{
          fontWeight: '600',
          '&:focus': { width: 'fit-content' },
          mb: '1rem',
          ...titleSx,
        }}
      />
      <Divider
        sx={{
          mb: '1.25rem',
          width: '100%',
        }}
      />
      <Grid container id={`candidate-profile-${title}-details`}>
        {Object.keys(data)?.map((label: string, index: number) => {
          return (
            <Grid
              key={`${label}-${index}`}
              item
              sm={12}
              md={gridLayout?.[index] ?? 6}
              id='candidate-{label}-container'
            >
              {data[label]?.map((item: any, j: number) => {
                return (
                  <LabelValuePair
                    key={`${item.label}-${j}`}
                    id={`${item.label}-Id`}
                    label={item.label}
                    value={item.value}
                    {...pairDisplayStyle}
                  />
                );
              })}
            </Grid>
          );
        })}
      </Grid>
    </Grid>
  );
};

const CandidateApplicationDetails: React.FC<
  ICandidateProfileApplicationDetails
> = ({ data: candidateApplicationHistory, loading }) => {
  const [selectedApplication, setSelectedApplication] =
    useState<ICandidateProfileApplicationHistory>();

  useEffect(() => {
    /* istanbul ignore next */
    if (candidateApplicationHistory) {
      setSelectedApplication(candidateApplicationHistory);
    }
  }, [candidateApplicationHistory]);

  const appliedApplicationDetails =
    mapAppliedCandidateDetailsData(selectedApplication);
  const candidateApplicationForm =
    mapCandidateApplicationFormData(selectedApplication);

  return (
    <Box
      id='candidate-profile-application-container'
      data-testid='candidate-profile-application-container'
      sx={{
        mt: '1.5rem',
        mb: '1.375rem',
      }}
    >
      {!loading ? (
        <>
          {!!selectedApplication && (
            <Grid
              id='candidate-profile-applied-for-application-form-container'
              container
              direction='column'
            >
              <CandidateApplicationDetailsBlock
                title='appliedFor'
                titleSx={{ mb: '0.5rem' }}
                data={appliedApplicationDetails}
                gridLayout={[7, 5]}
              />
              <CandidateApplicationDetailsBlock
                title='applicationForm'
                data={candidateApplicationForm}
                titleSx={{ mb: '0.5rem' }}
                gridLayout={[7, 5]}
              />
            </Grid>
          )}
        </>
      ) : (
        <CandidateApplicationsActivitySkeleton />
      )}
    </Box>
  );
};

export default CandidateApplicationDetails;
